import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QR_LOCATION_TYPES = [
  { value: 'table', label: 'Masa', icon: '🪑' },
  { value: 'counter', label: 'Kasa', icon: '💳' },
  { value: 'poster', label: 'Poster', icon: '📄' },
  { value: 'event', label: 'Etkinlik', icon: '🎉' },
  { value: 'in_app', label: 'Uygulama İçi', icon: '📱' },
  { value: 'window', label: 'Vitrin', icon: '🪟' },
  { value: 'menu', label: 'Menü', icon: '📋' },
  { value: 'other', label: 'Diğer', icon: '📍' },
];

const PartnerDetailModal = ({ partner, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('branches');
  const [branches, setBranches] = useState([]);
  const [qrLocations, setQrLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Branch form
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [branchForm, setBranchForm] = useState({ name: '', address: '', city: '', notes: '' });
  const [editingBranch, setEditingBranch] = useState(null);
  
  // QR form
  const [showQRForm, setShowQRForm] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [qrForm, setQrForm] = useState({ name: '', location_type: 'table', description: '' });
  
  // QR Preview
  const [previewQR, setPreviewQR] = useState(null);
  
  // Copied feedback
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (partner) {
      fetchData();
    }
  }, [partner]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch branches
      const branchRes = await axios.get(
        `${API_URL}/api/growth-hub/partners/${partner.id}/branches`,
        { headers }
      );
      setBranches(branchRes.data.branches || []);
      
      // Fetch all QR locations
      const qrRes = await axios.get(
        `${API_URL}/api/growth-hub/partners/${partner.id}/qr-locations`,
        { headers }
      );
      setQrLocations(qrRes.data.qr_locations || []);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBranch = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingBranch) {
        await axios.put(
          `${API_URL}/api/growth-hub/branches/${editingBranch.id}`,
          branchForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/growth-hub/partners/${partner.id}/branches`,
          branchForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowBranchForm(false);
      setEditingBranch(null);
      setBranchForm({ name: '', address: '', city: '', notes: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to save branch:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('Bu şubeyi pasifleştirmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/growth-hub/branches/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to delete branch:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateQR = async (e) => {
    e.preventDefault();
    if (!selectedBranch) {
      alert('Lütfen bir şube seçin');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/growth-hub/branches/${selectedBranch.id}/qr-locations`,
        qrForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowQRForm(false);
      setSelectedBranch(null);
      setQrForm({ name: '', location_type: 'table', description: '' });
      setPreviewQR(res.data.qr_location);
      fetchData();
    } catch (error) {
      console.error('Failed to create QR:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteQR = async (qrId) => {
    if (!window.confirm('Bu QR kodunu pasifleştirmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/growth-hub/qr-locations/${qrId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to delete QR:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadQRCode = (qrLocation) => {
    const svg = document.getElementById(`qr-svg-${qrLocation.id}`);
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-${qrLocation.qr_code}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const getQRTypeIcon = (type) => {
    const found = QR_LOCATION_TYPES.find(t => t.value === type);
    return found ? found.icon : '📍';
  };

  const getQRTypeLabel = (type) => {
    const found = QR_LOCATION_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  // Body scroll lock - must be before any conditional returns
  React.useEffect(() => {
    if (partner) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [partner]);

  if (!partner) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(10,10,20,0.98)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Premium Header */}
        <div style={{
          padding: '24px 28px',
          borderBottom: '1px solid rgba(139,92,246,0.2)',
          background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, transparent 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '22px', fontWeight: '700', letterSpacing: '-0.3px' }}>
              {partner.name}
            </h2>
            <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ background: 'rgba(139,92,246,0.15)', padding: '3px 10px', borderRadius: '6px', color: '#c084fc', fontSize: '11px', fontWeight: '500' }}>Partner Yönetimi</span>
              <span style={{ color: 'rgba(255,255,255,0.45)' }}>•</span>
              <span>Şube & QR Kodları</span>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.8)',
              padding: '10px 18px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
            }}
          >
            Kapat
          </button>
        </div>

        {/* Premium Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '16px 28px',
          background: 'rgba(0,0,0,0.25)',
          borderBottom: '1px solid rgba(255,255,255,0.04)'
        }}>
          {[
            { id: 'branches', label: 'Şubeler', count: branches.length },
            { id: 'qr', label: 'QR Kodları', count: qrLocations.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                background: activeTab === tab.id 
                  ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(99, 102, 241, 0.15))' 
                  : 'transparent',
                border: activeTab === tab.id 
                  ? '1px solid rgba(139, 92, 246, 0.35)' 
                  : '1px solid transparent',
                borderRadius: '10px',
                color: activeTab === tab.id ? '#c084fc' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
              <span style={{
                background: activeTab === tab.id ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.08)',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
              Yükleniyor...
            </div>
          ) : activeTab === 'branches' ? (
            <div>
              {/* Add Branch Button + Quick Templates */}
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                {/* Quick Branch Templates */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['Masa 1', 'Masa 2', 'Masa 3', 'VIP', 'Bahçe', 'Kasa'].map((template) => (
                    <button
                      key={template}
                      onClick={() => {
                        setBranchForm({ name: template, address: '', city: '', notes: '' });
                        setEditingBranch(null);
                        setShowBranchForm(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(139, 92, 246, 0.1)',
                        border: '1px solid rgba(139, 92, 246, 0.2)',
                        borderRadius: '6px',
                        color: '#c084fc',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      + {template}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setEditingBranch(null);
                    setBranchForm({ name: '', address: '', city: '', notes: '' });
                    setShowBranchForm(true);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  + Yeni Şube
                </button>
              </div>

              {/* Branch List */}
              {branches.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '48px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏪</div>
                  <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '15px', fontWeight: '500' }}>
                    Henüz şube eklenmemiş
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginTop: '8px' }}>
                    Yeni Şube butonuna tıklayarak başlayın
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '14px' }}>
                  {branches.map(branch => (
                    <div
                      key={branch.id}
                      style={{
                        background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                        border: `1px solid rgba(255,255,255,${branch.status === 'active' ? '0.1' : '0.05'})`,
                        borderRadius: '16px',
                        padding: '20px',
                        opacity: branch.status === 'active' ? 1 : 0.5,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>{branch.name}</span>
                            <span style={{
                              background: branch.status === 'active' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                              color: branch.status === 'active' ? '#22c55e' : '#ef4444',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              border: `1px solid ${branch.status === 'active' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`
                            }}>
                              {branch.status === 'active' ? 'Aktif' : 'Pasif'}
                            </span>
                          </div>
                          {branch.address && (
                            <div style={{ 
                              color: 'rgba(255,255,255,0.5)', 
                              fontSize: '13px', 
                              marginTop: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{ color: 'rgba(255,255,255,0.45)' }}>📍</span>
                              {branch.address}{branch.city ? `, ${branch.city}` : ''}
                            </div>
                          )}
                          <div style={{ 
                            display: 'flex', 
                            gap: '20px', 
                            marginTop: '12px',
                            padding: '10px 14px',
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '10px',
                            width: 'fit-content'
                          }}>
                            <span style={{ 
                              color: '#8b5cf6', 
                              fontSize: '13px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: '500' }}>QR</span>
                              {branch.total_qr_codes}
                            </span>
                            <span style={{ 
                              color: '#22c55e', 
                              fontSize: '13px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: '500' }}>Tarama</span>
                              {branch.total_scans}
                            </span>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => {
                              setSelectedBranch(branch);
                              setShowQRForm(true);
                            }}
                            style={{
                              padding: '10px 14px',
                              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(16, 185, 129, 0.1))',
                              border: '1px solid rgba(34, 197, 94, 0.25)',
                              borderRadius: '8px',
                              color: '#22c55e',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            + QR Ekle
                          </button>
                          <button
                            onClick={() => {
                              setEditingBranch(branch);
                              setBranchForm({
                                name: branch.name,
                                address: branch.address || '',
                                city: branch.city || '',
                                notes: branch.notes || ''
                              });
                              setShowBranchForm(true);
                            }}
                            style={{
                              padding: '10px 14px',
                              background: 'rgba(255,255,255,0.06)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '8px',
                              color: 'rgba(255,255,255,0.7)',
                              cursor: 'pointer',
                              fontSize: '12px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Düzenle
                          </button>
                          <button
                            onClick={() => handleDeleteBranch(branch.id)}
                            style={{
                              padding: '10px 14px',
                              background: 'rgba(239, 68, 68, 0.08)',
                              border: '1px solid rgba(239, 68, 68, 0.15)',
                              borderRadius: '8px',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '12px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Sil
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* QR Location List */}
              {qrLocations.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>📱</div>
                  <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0 }}>
                    Henüz QR kodu eklenmemiş
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', marginTop: '8px' }}>
                    Önce bir şube ekleyin, sonra o şubeye QR kodu ekleyebilirsiniz
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                  {qrLocations.map(qr => {
                    const branch = branches.find(b => b.id === qr.branch_id);
                    return (
                      <div
                        key={qr.id}
                        style={{
                          background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                          border: `1px solid rgba(255,255,255,${qr.status === 'active' ? '0.1' : '0.05'})`,
                          borderRadius: '16px',
                          padding: '20px',
                          opacity: qr.status === 'active' ? 1 : 0.5,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                          {/* QR Code Preview */}
                          <div style={{
                            background: 'white',
                            padding: '10px',
                            borderRadius: '12px',
                            flexShrink: 0,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                          }}>
                            <QRCodeSVG 
                              id={`qr-svg-${qr.id}`}
                              value={qr.qr_url}
                              size={90}
                              level="M"
                            />
                          </div>
                          
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '18px' }}>{getQRTypeIcon(qr.location_type)}</span>
                              <span style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>{qr.name}</span>
                            </div>
                            
                            <div style={{ 
                              color: 'rgba(255,255,255,0.5)', 
                              fontSize: '12px', 
                              marginTop: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{ 
                                background: 'rgba(139,92,246,0.15)', 
                                padding: '2px 8px', 
                                borderRadius: '4px',
                                color: '#a78bfa',
                                fontSize: '11px'
                              }}>{getQRTypeLabel(qr.location_type)}</span>
                              <span style={{ color: 'rgba(255,255,255,0.45)' }}>•</span>
                              <span>{branch?.name || 'Şube bulunamadı'}</span>
                            </div>
                            
                            <div style={{ 
                              color: 'rgba(255,255,255,0.45)', 
                              fontSize: '10px', 
                              marginTop: '8px',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              background: 'rgba(0,0,0,0.2)',
                              padding: '6px 8px',
                              borderRadius: '6px'
                            }}>
                              {qr.qr_code}
                            </div>
                            
                            <div style={{ 
                              color: '#22c55e', 
                              fontSize: '13px', 
                              marginTop: '10px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <span style={{ 
                                width: '8px', 
                                height: '8px', 
                                borderRadius: '50%', 
                                background: '#22c55e',
                                boxShadow: '0 0 8px rgba(34,197,94,0.5)'
                              }}></span>
                              {qr.total_scans} tarama
                            </div>
                          </div>
                        </div>
                        
                        {/* Premium Actions - 2 Rows */}
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: '8px', 
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: '1px solid rgba(255,255,255,0.06)'
                        }}>
                          {/* Row 1 */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => copyToClipboard(qr.qr_url, qr.id)}
                              style={{
                                flex: 1,
                                padding: '10px',
                                background: 'rgba(139, 92, 246, 0.1)',
                                border: '1px solid rgba(139, 92, 246, 0.2)',
                                borderRadius: '8px',
                                color: '#c084fc',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {copiedId === qr.id ? '✓ Kopyalandı' : 'URL Kopyala'}
                            </button>
                            <button
                              onClick={() => window.open(qr.qr_url, '_blank')}
                              style={{
                                padding: '10px 14px',
                                background: 'rgba(6, 182, 212, 0.1)',
                                border: '1px solid rgba(6, 182, 212, 0.2)',
                                borderRadius: '8px',
                                color: '#22d3ee',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              Landing Aç
                            </button>
                          </div>
                          {/* Row 2 */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => downloadQRCode(qr)}
                              style={{
                                flex: 1,
                                padding: '10px',
                                background: 'rgba(34, 197, 94, 0.1)',
                                border: '1px solid rgba(34, 197, 94, 0.2)',
                                borderRadius: '8px',
                                color: '#22c55e',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              PNG İndir
                            </button>
                            <button
                              onClick={() => {
                                const svg = document.getElementById(`qr-svg-${qr.id}`);
                                if (svg) {
                                  const svgData = new XMLSerializer().serializeToString(svg);
                                  const blob = new Blob([svgData], { type: 'image/svg+xml' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `qr-${qr.name || qr.id}.svg`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                }
                              }}
                              style={{
                                flex: 1,
                                padding: '10px',
                                background: 'rgba(251, 191, 36, 0.1)',
                                border: '1px solid rgba(251, 191, 36, 0.2)',
                                borderRadius: '8px',
                                color: '#fbbf24',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              SVG İndir
                            </button>
                            <button
                              onClick={() => handleDeleteQR(qr.id)}
                              style={{
                                padding: '10px 14px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.2)',
                                borderRadius: '8px',
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: '12px',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              Sil
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Branch Form Modal */}
        <AnimatePresence>
          {showBranchForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.85)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                zIndex: 10000
              }}
              onClick={() => setShowBranchForm(false)}
            >
              <motion.form
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                onClick={e => e.stopPropagation()}
                onSubmit={handleCreateBranch}
                style={{
                  background: '#1a1a2e',
                  borderRadius: '16px',
                  padding: '24px',
                  width: '100%',
                  maxWidth: '400px'
                }}
              >
                <h3 style={{ margin: '0 0 20px', color: 'white' }}>
                  {editingBranch ? '✏️ Şube Düzenle' : '🏪 Yeni Şube'}
                </h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Şube Adı *
                  </label>
                  <input
                    type="text"
                    value={branchForm.name}
                    onChange={e => setBranchForm({ ...branchForm, name: e.target.value })}
                    required
                    placeholder="Örn: Nişantaşı Şubesi"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Adres
                  </label>
                  <input
                    type="text"
                    value={branchForm.address}
                    onChange={e => setBranchForm({ ...branchForm, address: e.target.value })}
                    placeholder="Örn: Valikonağı Cad. No:123"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Şehir
                  </label>
                  <input
                    type="text"
                    value={branchForm.city}
                    onChange={e => setBranchForm({ ...branchForm, city: e.target.value })}
                    placeholder="Örn: İstanbul"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => setShowBranchForm(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'rgba(255,255,255,0.7)',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {editingBranch ? 'Güncelle' : 'Oluştur'}
                  </button>
                </div>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* QR Form Modal */}
        <AnimatePresence>
          {showQRForm && selectedBranch && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.85)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                zIndex: 10000
              }}
              onClick={() => setShowQRForm(false)}
            >
              <motion.form
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                onClick={e => e.stopPropagation()}
                onSubmit={handleCreateQR}
                style={{
                  background: '#1a1a2e',
                  borderRadius: '16px',
                  padding: '24px',
                  width: '100%',
                  maxWidth: '400px'
                }}
              >
                <h3 style={{ margin: '0 0 8px', color: 'white' }}>
                  📱 Yeni QR Kodu
                </h3>
                <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                  Şube: {selectedBranch.name}
                </p>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Konum Adı *
                  </label>
                  <input
                    type="text"
                    value={qrForm.name}
                    onChange={e => setQrForm({ ...qrForm, name: e.target.value })}
                    required
                    placeholder="Örn: Masa 1, Kasa, Giriş Posteri"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Konum Türü
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {QR_LOCATION_TYPES.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setQrForm({ ...qrForm, location_type: type.value })}
                        style={{
                          padding: '10px 8px',
                          background: qrForm.location_type === type.value 
                            ? 'rgba(139, 92, 246, 0.2)' 
                            : 'rgba(255,255,255,0.05)',
                          border: qrForm.location_type === type.value 
                            ? '1px solid rgba(139, 92, 246, 0.5)' 
                            : '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          color: qrForm.location_type === type.value ? '#c084fc' : 'rgba(255,255,255,0.6)',
                          cursor: 'pointer',
                          fontSize: '11px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>{type.icon}</span>
                        <span>{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Açıklama
                  </label>
                  <input
                    type="text"
                    value={qrForm.description}
                    onChange={e => setQrForm({ ...qrForm, description: e.target.value })}
                    placeholder="Örn: Pencere kenarı masa"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => setShowQRForm(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'rgba(255,255,255,0.7)',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'linear-gradient(135deg, #22c55e, #10b981)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    QR Oluştur
                  </button>
                </div>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* QR Preview Modal */}
        <AnimatePresence>
          {previewQR && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.85)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                zIndex: 10000
              }}
              onClick={() => setPreviewQR(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                onClick={e => e.stopPropagation()}
                style={{
                  background: '#1a1a2e',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center',
                  maxWidth: '350px'
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>✅</div>
                <h3 style={{ margin: '0 0 8px', color: 'white' }}>QR Kodu Oluşturuldu!</h3>
                <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                  {previewQR.name}
                </p>
                
                <div style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  display: 'inline-block',
                  marginBottom: '20px'
                }}>
                  <QRCodeSVG 
                    id={`qr-preview-${previewQR.id}`}
                    value={previewQR.qr_url}
                    size={180}
                    level="M"
                  />
                </div>
                
                <div style={{ 
                  color: 'rgba(255,255,255,0.4)', 
                  fontSize: '11px', 
                  marginBottom: '20px',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}>
                  {previewQR.qr_url}
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => copyToClipboard(previewQR.qr_url, 'preview')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'rgba(139, 92, 246, 0.2)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '10px',
                      color: '#c084fc',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {copiedId === 'preview' ? '✓ Kopyalandı' : '📋 URL Kopyala'}
                  </button>
                  <button
                    onClick={() => setPreviewQR(null)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'linear-gradient(135deg, #22c55e, #10b981)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Tamam
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default PartnerDetailModal;
